<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import countries from 'shared/constants/countries';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  email: { type: String, default: '' },
  additionalAttributes: { type: Object, default: () => ({}) },
  phoneNumber: { type: String, default: '' },
  thumbnail: { type: String, default: '' },
  isExpanded: { type: Boolean, default: false },
  isUpdating: { type: Boolean, default: false },
  orders: { type: Array, default: () => [] },
});

const emit = defineEmits(['toggle', 'updateContact', 'showContact']);

const { t } = useI18n();

const contactsFormRef = ref(null);

const getInitialContactData = () => ({
  id: props.id,
  name: props.name,
  email: props.email,
  phoneNumber: props.phoneNumber,
  additionalAttributes: props.additionalAttributes,
});

const contactData = ref(getInitialContactData());

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const countriesMap = computed(() => {
  return countries.reduce((acc, country) => {
    acc[country.code] = country;
    acc[country.id] = country;
    return acc;
  }, {});
});

const countryDetails = computed(() => {
  const attributes = props.additionalAttributes || {};
  const { country, countryCode, city } = attributes;

  if (!country && !countryCode) return null;

  const activeCountry =
    countriesMap.value[country] || countriesMap.value[countryCode];

  if (!activeCountry) return null;

  return {
    countryCode: activeCountry.id,
    city: city ? `${city},` : null,
    name: activeCountry.name,
  };
});

const formattedLocation = computed(() => {
  if (!countryDetails.value) return '';

  return [countryDetails.value.city, countryDetails.value.name]
    .filter(Boolean)
    .join(' ');
});

const formatDate = dateStr => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatCurrency = (cents, currencyCode = 'USD') => {
  if (!cents) return '0';
  const amount = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

const getStatusColor = status => {
  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    processing: 'bg-blue-100 text-blue-800',
    shipped: 'bg-purple-100 text-purple-800',
    delivered: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    refunded: 'bg-gray-100 text-gray-800',
  };
  return statusColors[status] || 'bg-gray-100 text-gray-800';
};

const getSourceIcon = source => {
  const icons = {
    etsy: 'i-simple-icons-etsy',
    tiktok: 'i-simple-icons-tiktok',
    shopee: 'i-simple-icons-shopee',
    lazada: 'i-simple-icons-lazada',
    kimkieuflower: 'i-ph-flower',
  };
  return icons[source] || 'i-ph-storefront';
};

const displayedOrders = computed(() => props.orders.slice(0, 3));
const hasMoreOrders = computed(() => props.orders.length > 3);

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const handleUpdateContact = () => {
  emit('updateContact', contactData.value);
};

const onClickExpand = () => {
  emit('toggle');
  contactData.value = getInitialContactData();
};

const onClickViewDetails = () => emit('showContact', props.id);
</script>

<template>
  <CardLayout :key="id" layout="row">
    <div class="flex items-center justify-start flex-1 gap-4">
      <Avatar :name="name" :src="thumbnail" :size="48" rounded-full />
      <div class="flex flex-col gap-0.5 flex-1">
        <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span class="text-base font-medium truncate text-n-slate-12">
            {{ name }}
          </span>
          <span class="inline-flex items-center gap-1">
            <span
              v-if="additionalAttributes?.companyName"
              class="i-ph-building-light size-4 text-n-slate-10 mb-0.5"
            />
            <span
              v-if="additionalAttributes?.companyName"
              class="text-sm truncate text-n-slate-11"
            >
              {{ additionalAttributes.companyName }}
            </span>
          </span>
        </div>
        <div class="flex flex-wrap items-center justify-start gap-x-3 gap-y-1">
          <div v-if="email" class="truncate max-w-72" :title="email">
            <span class="text-sm text-n-slate-11">
              {{ email }}
            </span>
          </div>
          <div v-if="email" class="w-px h-3 truncate bg-n-slate-6" />
          <span v-if="phoneNumber" class="text-sm truncate text-n-slate-11">
            {{ phoneNumber }}
          </span>
          <div v-if="phoneNumber" class="w-px h-3 truncate bg-n-slate-6" />
          <span
            v-if="countryDetails"
            class="inline-flex items-center gap-2 text-sm truncate text-n-slate-11"
          >
            <Flag :country="countryDetails.countryCode" class="size-3.5" />
            {{ formattedLocation }}
          </span>
          <div v-if="countryDetails" class="w-px h-3 truncate bg-n-slate-6" />
          <Button
            :label="t('CONTACTS_LAYOUT.CARD.VIEW_DETAILS')"
            variant="link"
            size="xs"
            @click="onClickViewDetails"
          />
        </div>
        <div v-if="orders.length" class="mt-3">
          <div class="flex items-center gap-2 mb-2">
            <span class="i-ph-shopping-bag text-sm text-n-slate-10" />
            <span class="text-xs font-semibold text-n-slate-11">
              {{ t('CONTACT_PANEL.ORDERS') }}
            </span>
            <span
              class="text-xs text-n-slate-9 bg-n-slate-3 px-1.5 py-0.5 rounded-full"
            >
              {{ orders.length }}
            </span>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div
              v-for="order in displayedOrders"
              :key="order.externalOrderId"
              class="group relative bg-white border border-n-slate-4 rounded-md p-2 hover:border-blue-300 hover:shadow-sm transition-all duration-200"
            >
              <router-link :to="order.orderLink" class="block" target="_blank">
                <div class="flex items-center gap-1.5 mb-1">
                  <span
                    :class="getSourceIcon(order.source)"
                    class="text-xs text-n-slate-10"
                  />
                  <span class="text-xs font-semibold text-blue-600 truncate">
                    #{{ order.externalOrderId }}
                  </span>
                  <span
                    :class="getStatusColor(order.status)"
                    class="inline-flex px-1.5 py-0.5 text-xs font-medium rounded text-center ml-auto"
                  >
                    {{
                      order.status.charAt(0).toUpperCase() +
                      order.status.slice(1)
                    }}
                  </span>
                </div>

                <div
                  class="flex items-center justify-between text-xs text-n-slate-10"
                >
                  <span class="flex items-center gap-1 font-medium">
                    <span class="i-ph-currency-dollar text-xs" />
                    {{ formatCurrency(order.totalCents, order.currencyCode) }}
                  </span>
                  <span class="flex items-center gap-1">
                    <span class="i-ph-calendar text-xs" />
                    <span v-if="order.paidAt">
                      {{ formatDate(order.paidAt) }}
                    </span>
                    <span v-else class="italic">
                      {{ t('CONTACT_PANEL.NOT_PAID') }}
                    </span>
                  </span>
                </div>
              </router-link>
            </div>
          </div>

          <div v-if="hasMoreOrders" class="text-center mt-2">
            <button
              class="text-xs text-blue-600 hover:text-blue-700 hover:underline font-medium"
              @click="onClickViewDetails"
            >
              {{
                t('CONTACT_PANEL.VIEW_ALL_ORDERS', {
                  count: orders.length - 3,
                })
              }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <Button
      icon="i-lucide-chevron-down"
      variant="ghost"
      color="slate"
      size="xs"
      :class="{ 'rotate-180': isExpanded }"
      @click="onClickExpand"
    />

    <template #after>
      <transition
        enter-active-class="overflow-hidden transition-all duration-300 ease-out"
        leave-active-class="overflow-hidden transition-all duration-300 ease-in"
        enter-from-class="overflow-hidden opacity-0 max-h-0"
        enter-to-class="opacity-100 max-h-[690px] sm:max-h-[470px] md:max-h-[410px]"
        leave-from-class="opacity-100 max-h-[690px] sm:max-h-[470px] md:max-h-[410px]"
        leave-to-class="overflow-hidden opacity-0 max-h-0"
      >
        <div v-show="isExpanded" class="w-full">
          <div class="flex flex-col gap-6 p-6 border-t border-n-strong">
            <ContactsForm
              ref="contactsFormRef"
              :contact-data="contactData"
              @update="handleFormUpdate"
            />
            <div>
              <Button
                :label="
                  t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')
                "
                size="sm"
                :is-loading="isUpdating"
                :disabled="isUpdating || isFormInvalid"
                @click="handleUpdateContact"
              />
            </div>
          </div>
        </div>
      </transition>
    </template>
  </CardLayout>
</template>
